import { APP_ROUTES, DEFAULT_AUTHENTICATED_ROUTE } from "@/constants/app-routes";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { isEntryRoot, isMainAppRoute } from "@/router/route-classes";
import { resolvePostAuthRoute } from "@/utils/auth/resolve-post-auth-route";
import { useAuth } from "@clerk/clerk-react";
import { useIonRouter } from "@ionic/react";
import { memo, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * The single side-effect-only guard that owns state-driven navigation for an
 * authenticated app. Mounted once globally next to PaywallGuard. Renders nothing.
 *
 * It is the *only* actor that redirects based on auth + onboarding state, which is
 * what makes the routing loop-free: previously `RootRedirect` (signed-in → /overview)
 * and `OnboardingGate` (un-onboarded → /get-started) were independent, always-mounted
 * effects with contradictory beliefs, and they oscillated for signed-in-but-un-onboarded
 * users. Concentrating the decision here removes the contradiction.
 *
 * Responsibility split:
 * - Signed-OUT on a private route → owned by PrivateAuthGuard (it also signs out).
 * - `/splash-screen` → owns its own branded 2s delay + destination.
 * - This guard owns: the bare entry (`/`) and pulling un-onboarded users off main-app
 *   routes. The signed-in destination is computed by `resolvePostAuthRoute`.
 */
export const AuthRouteGuard = memo(function AuthRouteGuard() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user, isLoading: isUserLoading, isError } = useGetUser();
  const location = useLocation();
  const navigation = useIonRouter();

  // De-dupe latch: prevents a second push to the same target during the window
  // between calling push() and `location` reflecting it (e.g. when the [USER]
  // query refetches and re-runs this effect mid-transition). Reset on arrival so a
  // later return to a route that needs enforcing (back to `/`) can fire again.
  const lastPushedRef = useRef<string | null>(null);

  const path = location.pathname;
  const hasUser = Boolean(user);
  const isOnboardingCompleted = user?.isOnboardingCompleted;

  useEffect(() => {
    if (path === lastPushedRef.current) lastPushedRef.current = null;
    if (!isLoaded) return;

    const go = (target: string) => {
      if (path === target || lastPushedRef.current === target) return;
      lastPushedRef.current = target;
      navigation.push(target, "none", "replace");
    };

    // Signed out: only own the bare entry. Private routes belong to PrivateAuthGuard,
    // /splash-screen drives itself, and auth screens are already a valid destination.
    if (!isSignedIn) {
      if (isEntryRoot(path)) go(APP_ROUTES.SPLASH_SCREEN);
      return;
    }

    // Signed in on the bare entry but with a broken/missing user record: hand off to
    // /overview where PrivateAuthGuard owns recovery (sign-out / RevenueCat). Without
    // this, a cleared USER_KEY would strand the user on the root spinner forever.
    if (isEntryRoot(path) && (isError || (!isUserLoading && !hasUser))) {
      go(DEFAULT_AUTHENTICATED_ROUTE);
      return;
    }
    // While the user fetch is unresolved or errored, onboarding state is unknown —
    // redirecting now would flash onboarding at users who are in fact onboarded.
    if (isUserLoading || isError || !hasUser) return;

    const target = resolvePostAuthRoute(isOnboardingCompleted);
    const enforce =
      target === APP_ROUTES.ONBOARDING_START
        ? isMainAppRoute(path) || isEntryRoot(path) // un-onboarded: pull off main-app + entry
        : isEntryRoot(path); //                        onboarded: only from the entry root

    if (enforce) go(target);
  }, [
    isLoaded,
    isSignedIn,
    isUserLoading,
    isError,
    hasUser,
    isOnboardingCompleted,
    path,
    navigation,
  ]);

  return null;
});
